/**
 * client/render/world.ts — loads map.glb and turns it into a handful of batched draws with the BO2
 * lighting model (glsl.ts). OWNER: RENDER agent.
 *
 * Batching: every world primitive / static-model instance / entity model is appended into a "draw group"
 * keyed by (lighting kind, alpha mode + cutoff, blend state, cull side, polygon offset, sortKey,
 * texture size). All colour maps of one group live in one 2D texture array (texarray.ts) and a per-vertex
 * layer index selects the texture; normal maps go into a parallel array with the same layer index.
 * Hijacked: 662 materials / 2768 meshes -> ~140 groups (draw calls when everything is in view).
 *
 * Static-model distance culling is the engine's per-instance `cullDist` (GfxStaticModelDrawInst, node
 * extras.bo2.cullDist), done per vertex in the vertex shader (a culled instance collapses to nothing), so
 * merging does not lose it. Only LOD0 exists in the export (tools/map/README.md "Known gaps").
 *
 * Sun: a per-vertex flag (bit 1 of the aPL light mask, primary light 1 = the sun), so sun-lit and
 * unlit-by-sun surfaces share groups and texture layers. World primitives use extras.sunLit; static models are sun lit when their primaryLight equals
 * meta.sunPrimaryLightIndex (map-hijacked.md: the sun is not baked; the engine adds it in real time to
 * surfaces whose primaryLightIndex == sunPrimaryLightIndex). Entity models use their primitive's sunLit.
 * `wpc/shadowcaster` (tool) surfaces are invisible but cast sun shadows.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { BO2_FRAG_COMMON, BO2_LIGHTMAP, MAX_PRIMARY_LIGHTS, type Bo2Uniforms } from './glsl';
import { TextureArrayBuilder, imageSize, resizeImage, type LayerSource } from './texarray';

type LightKind = 'lm' | 'vl' | 'unlit';
type AlphaMode = 'OPAQUE' | 'MASK' | 'BLEND';

interface Bo2MatExtras {
  kind?: string;
  sortKey?: number;
  state?: { srcBlendRgb?: string; dstBlendRgb?: string; blendOpRgb?: string; depthWrite?: boolean; cullFace?: string; polygonOffset?: string; alphaTest?: string };
  material?: string;
  constants?: Record<string, number[]>;
}

interface PrimRef {
  mesh: THREE.Mesh;
  /** Bitmask of primary (spot/omni) light indices >= 2 lighting this primitive / instance. */
  plMask: number;
  layer: number;
  cull: number; // cull distance (0 = never)
  center: THREE.Vector3;
}

interface Group {
  key: string;
  light: LightKind;
  alpha: AlphaMode;
  cutoff: number;
  doubleSided: boolean;
  polyOffset: number;
  blend: string; // 'none' | 'premul' | 'alpha' | 'add' | 'mul'
  depthWrite: boolean;
  sortKey: number;
  model: boolean; // has cull attribute
  w: number;
  h: number;
  colors: TextureArrayBuilder;
  normals: TextureArrayBuilder | null;
  prims: PrimRef[];
  shadowOnly: boolean;
  primaryLights: boolean;
}

export interface WorldStats {
  groups: number;
  layers: number;
  textureMB: number;
  vertices: number;
  triangles: number;
  waterMeshes: number;
  loadMs: number;
}

/** A light probe point for dynamic models (avg static-model irradiance, CoD-free three space). */
export interface LightProbe { pos: THREE.Vector3; e: THREE.Vector3 }

export interface WorldBuildOptions {
  normalMaps: boolean;
  anisotropy: number;
  sunPrimaryLightIndex: number;
  lightmapScale: number;
  onProgress?: (frac: number, label: string) => void;
}

export interface BuiltWorld {
  root: THREE.Group;
  /** Opaque + mask casters for the static sun shadow map (shadow-pass materials attached in userData). */
  casters: THREE.Mesh[];
  water: THREE.Mesh[];
  probes: LightProbe[];
  stats: WorldStats;
  /** World-space bounds of all visible surfaces (three space). */
  bounds: THREE.Box3;
}

const FLAT_NORMAL: [number, number, number, number] = [128, 128, 255, 255];
const WHITE: [number, number, number, number] = [255, 255, 255, 255];

function blendKind(st: Bo2MatExtras['state'], alpha: AlphaMode): string {
  if (alpha !== 'BLEND') return 'none';
  const s = st?.srcBlendRgb, d = st?.dstBlendRgb;
  if (s === 'one' && d === 'invsrcalpha') return 'premul';
  if (s === 'one' && d === 'one') return 'add';
  if (s === 'zero' && d === 'srccolor') return 'mul';
  if (s === 'one' && d === 'zero') return 'none';
  return 'alpha';
}

function findBo2Node(o: THREE.Object3D): Record<string, unknown> | null {
  let n: THREE.Object3D | null = o;
  while (n) {
    const b = (n.userData as { bo2?: Record<string, unknown> }).bo2;
    if (b) return b;
    n = n.parent;
  }
  return null;
}

export async function loadWorld(
  url: string, renderer: THREE.WebGLRenderer, U: Bo2Uniforms, opts: WorldBuildOptions,
): Promise<BuiltWorld> {
  const t0 = performance.now();
  const gltf = await new GLTFLoader().loadAsync(url, (e) => {
    if (e.total) opts.onProgress?.(0.75 * (e.loaded / e.total), 'map.glb');
  });
  opts.onProgress?.(0.76, 'batching');
  const root = gltf.scene;
  root.updateMatrixWorld(true);

  const groups = new Map<string, Group>();
  const water: THREE.Mesh[] = [];
  const probes: LightProbe[] = [];
  const resizeJobs: Promise<void>[] = [];
  const tmpV = new THREE.Vector3();
  const bounds = new THREE.Box3();

  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const src = mesh.material as THREE.MeshStandardMaterial;
    const ex: Bo2MatExtras = ((src.userData as { bo2?: Bo2MatExtras }).bo2) ?? {};
    const g = mesh.geometry;
    const node = findBo2Node(mesh) ?? {};
    const nodeKind = node.kind as string | undefined;
    if (ex.kind === 'sky' || mesh.name.startsWith('skybox')) return;
    const shadowOnly = ex.kind === 'tool';
    if (shadowOnly && !(ex.material ?? src.name).includes('shadowcaster')) return;
    if (ex.kind === 'water') { water.push(mesh); return; }

    const hasLm = !!g.attributes.uv1;
    const hasLight = !!g.attributes._light;
    let light: LightKind = ex.kind === 'unlit' ? 'unlit' : hasLm ? 'lm' : hasLight ? 'vl' : 'unlit';
    if (light === 'lm' && !U.uLmPacked.value) light = hasLight ? 'vl' : 'unlit';
    const gud = g.userData as { sunLit?: boolean };
    let sun: boolean;
    if (nodeKind === 'static_model' && typeof node.primaryLight === 'number') sun = node.primaryLight === opts.sunPrimaryLightIndex;
    else sun = gud.sunLit === true;
    if (light === 'unlit') sun = false;
    const alpha: AlphaMode = src.transparent ? 'BLEND' : src.alphaTest > 0 ? 'MASK' : 'OPAQUE';
    const st = ex.state;
    const blend = blendKind(st, alpha);
    const depthWrite = st?.depthWrite ?? alpha !== 'BLEND';
    const doubleSided = src.side === THREE.DoubleSide;
    const polyOffset = st?.polygonOffset === 'offset1' ? 1 : st?.polygonOffset === 'offset2' ? 2 : 0;
    const map = src.map;
    const img = map?.image as TexImageSource | undefined;
    const [w, h] = img ? imageSize(img) : [4, 4];
    const model = nodeKind === 'static_model';
    const cull = model ? Number(node.cullDist ?? 0) : 0;
    const sortKey = alpha === 'BLEND' ? (ex.sortKey ?? 0) : 0;
    const key = [light, alpha, src.alphaTest || 0, blend, depthWrite ? 'w' : '-', doubleSided ? 'd' : '-', polyOffset, sortKey, model ? 'm' : '-', shadowOnly ? 'S' : '-', `${w}x${h}`].join('|');
    let grp = groups.get(key);
    if (!grp) {
      grp = {
        key, light, alpha, cutoff: src.alphaTest || 0.5, doubleSided, polyOffset, blend, depthWrite, sortKey, model, w, h,
        colors: new TextureArrayBuilder(w, h),
        // normal maps at half the colour resolution (own array size; same layer index): 1/4 the VRAM of full-res
        // normals (~80 MB instead of ~330 MB on Hijacked). ESTIMATE-level detail loss, documented.
        normals: opts.normalMaps && light !== 'unlit' && !shadowOnly ? new TextureArrayBuilder(Math.max(4, w >> 1), Math.max(4, h >> 1)) : null,
        prims: [], shadowOnly, primaryLights: false,
      };
      groups.set(key, grp);
    }
    const nimg = src.normalMap?.image as TexImageSource | undefined;
    const pairKey = `${img ? map!.uuid : 'white'}|${grp.normals && nimg ? src.normalMap!.uuid : 'flat'}`;
    const before = grp.colors.count;
    const layer = grp.colors.add(pairKey, img ?? { flat: WHITE });
    if (grp.normals && grp.colors.count > before) {
      if (nimg) {
        const [nw, nh] = imageSize(nimg);
        const ng = grp.normals;
        if (nw === ng.width && nh === ng.height) ng.add(pairKey, nimg);
        else {
          const slot = ng.add(pairKey, { flat: FLAT_NORMAL });
          resizeJobs.push(resizeImage(nimg, ng.width, ng.height).then((r) => { ng.layers[slot] = r; }));
        }
      } else grp.normals.add(pairKey, { flat: FLAT_NORMAL });
    }
    const center = new THREE.Vector3();
    mesh.getWorldPosition(center);
    // primary spot/omni lights: static models carry one index, world primitives the list of their surfaces'
    let plMask = sun ? 2 : 0;
    if (light !== 'unlit') {
      const list: number[] = model && typeof node.primaryLight === 'number' ? [node.primaryLight as number] : ((g.userData as { primaryLights?: number[] }).primaryLights ?? []);
      for (const li of list) if (li >= 2 && li < MAX_PRIMARY_LIGHTS) plMask |= 1 << li;
    }
    if (plMask) grp.primaryLights = true;
    grp.prims.push({ mesh, layer, cull, center, plMask });
    if (!shadowOnly) {
      g.computeBoundingBox();
      if (g.boundingBox) bounds.union(g.boundingBox.clone().applyMatrix4(mesh.matrixWorld));
    }
    // light probe for players: mean irradiance of this static model instance [ESTIMATE, see players.ts]
    if (model && hasLight) {
      const la = g.attributes._light as THREE.BufferAttribute;
      const e = new THREE.Vector3();
      const n = la.count, step = Math.max(1, Math.floor(n / 64));
      let c = 0;
      for (let i = 0; i < n; i += step) { e.x += la.getX(i); e.y += la.getY(i); e.z += la.getZ(i); c++; }
      e.multiplyScalar(opts.lightmapScale / Math.max(1, c));
      probes.push({ pos: center.clone(), e });
    }
  });
  void tmpV;
  await Promise.all(resizeJobs);
  opts.onProgress?.(0.85, 'textures');

  const out = new THREE.Group();
  out.name = 'bo2_world';
  const casters: THREE.Mesh[] = [];
  let vertices = 0, triangles = 0, layers = 0, gi = 0, texBytes = 0;
  const groupList = [...groups.values()];
  for (const grp of groupList) {
    const geo = buildGroupGeometry(grp);
    vertices += geo.attributes.position.count;
    triangles += (geo.index?.count ?? 0) / 3;
    layers += grp.colors.count;
    texBytes += grp.colors.count * grp.w * grp.h * 4 * 4 / 3 + (grp.normals ? grp.normals.count * grp.normals.width * grp.normals.height * 4 * 4 / 3 : 0);
    const colorArr = grp.colors.build(renderer, opts.anisotropy);
    const normalArr = grp.normals ? grp.normals.build(renderer, opts.anisotropy) : null;
    const mat = grp.shadowOnly ? null : createWorldMaterial(grp, colorArr, normalArr, U, (geo.userData.cullTex as THREE.DataTexture | undefined) ?? null);
    const depthMat = grp.alpha === 'BLEND' ? null : createDepthMaterial(grp, colorArr);
    const mesh = new THREE.Mesh(geo, mat ?? depthMat!);
    mesh.name = `grp_${grp.key}`;
    mesh.matrixAutoUpdate = false;
    mesh.renderOrder = grp.alpha === 'BLEND' ? 10 + grp.sortKey : 0;
    mesh.userData.depthMaterial = depthMat;
    mesh.userData.group = grp.key;
    mesh.visible = !grp.shadowOnly;
    if (!grp.shadowOnly) out.add(mesh);
    if (depthMat) casters.push(mesh);
    if (++gi % 8 === 0) opts.onProgress?.(0.85 + 0.12 * (gi / groupList.length), 'textures');
  }
  // free the source glTF images (now on the GPU inside the arrays)
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || water.includes(m)) return;
    m.geometry.dispose();
    const mm = m.material as THREE.MeshStandardMaterial;
    for (const t of [mm.map, mm.normalMap]) {
      const im = t?.image as ImageBitmap | undefined;
      if (im && typeof im.close === 'function') im.close();
      t?.dispose();
    }
    mm.dispose();
  });

  const waterMeshes = water.map((m) => createWaterMesh(m, U));
  for (const w of waterMeshes) out.add(w);

  return {
    root: out, casters, water: waterMeshes, probes, bounds,
    stats: { groups: groups.size, layers, textureMB: Math.round(texBytes / 1048576), vertices, triangles: Math.round(triangles), waterMeshes: waterMeshes.length, loadMs: Math.round(performance.now() - t0) },
  };
}

// ---------------------------------------------------------------------------------------------
// geometry merge
// ---------------------------------------------------------------------------------------------
/** Drop the CPU copy of an attribute once it is on the GPU (geometry is static). */
function freeAfterUpload(this: THREE.BufferAttribute): void {
  (this as unknown as { array: ArrayLike<number> | null }).array = null;
}

const CULL_TEX_W = 256;

function buildGroupGeometry(grp: Group): THREE.BufferGeometry {
  let nv = 0, ni = 0;
  // NOTE: the exported LOD0 meshes are genuinely unwelded (hard normals / uv seams: ~2 verts per triangle,
  // checked with numpy: unique (pos,normal,uv,colour) == vertex count), so no welding pass.
  for (const p of grp.prims) {
    const g = p.mesh.geometry;
    nv += g.attributes.position.count;
    ni += g.index ? g.index.count : g.attributes.position.count;
  }
  const pos = new Float32Array(nv * 3);
  const nrm = new Int8Array(nv * 4);
  const uv = new Float32Array(nv * 2);
  const col = new Uint8Array(nv * 4);
  const lay = new Uint16Array(nv);
  const uv1 = grp.light === 'lm' ? new Float32Array(nv * 2) : null;
  const lit = grp.light === 'vl' ? new Uint8Array(nv * 4) : null;
  // per-vertex instance index into the group's cull table (center xyz, cullDist) — 2 bytes/vertex
  const inst = grp.model ? new Uint16Array(nv) : null;
  const plm = grp.primaryLights ? new Float32Array(nv) : null;
  const cullTable: number[] = [];
  const idx = new Uint32Array(ni);
  const v = new THREE.Vector3();
  const nm = new THREE.Matrix3();
  let vo = 0, io = 0;
  for (let k = 0; k < grp.prims.length; k++) {
    const p = grp.prims[k];
    const g = p.mesh.geometry;
    const instIndex = cullTable.length / 4;
    if (inst) cullTable.push(p.center.x, p.center.y, p.center.z, p.cull);
    const m = p.mesh.matrixWorld;
    nm.getNormalMatrix(m);
    const P = g.attributes.position as THREE.BufferAttribute;
    const N = g.attributes.normal as THREE.BufferAttribute | undefined;
    const T = g.attributes.uv as THREE.BufferAttribute | undefined;
    const T1 = g.attributes.uv1 as THREE.BufferAttribute | undefined;
    const C = g.attributes.color as THREE.BufferAttribute | undefined;
    const L = g.attributes._light as THREE.BufferAttribute | undefined;
    const src = p.mesh.material as THREE.MeshStandardMaterial;
    const fr = src.color?.r ?? 1, fg = src.color?.g ?? 1, fb = src.color?.b ?? 1;
    const fa = src.opacity ?? 1;
    // baseColorFactor is linear in glTF (three keeps it linear); the BO2 colour path squares the
    // (texture * vertex colour) product, so fold sqrt(factor) into the vertex colour to keep it exact.
    const sr = Math.sqrt(fr), sg = Math.sqrt(fg), sb = Math.sqrt(fb);
    const nu = P.count;
    for (let i = 0; i < nu; i++) {
      const o = vo + i;
      v.fromBufferAttribute(P, i).applyMatrix4(m);
      pos[o * 3] = v.x; pos[o * 3 + 1] = v.y; pos[o * 3 + 2] = v.z;
      if (N) { v.fromBufferAttribute(N, i).applyMatrix3(nm).normalize(); } else v.set(0, 1, 0);
      nrm[o * 4] = Math.round(v.x * 127); nrm[o * 4 + 1] = Math.round(v.y * 127); nrm[o * 4 + 2] = Math.round(v.z * 127);
      if (T) { uv[o * 2] = T.getX(i); uv[o * 2 + 1] = T.getY(i); }
      if (uv1 && T1) { uv1[o * 2] = T1.getX(i); uv1[o * 2 + 1] = T1.getY(i); }
      let r = 1, gg = 1, b = 1, a = 1;
      if (C) { r = C.getX(i); gg = C.getY(i); b = C.getZ(i); a = C.itemSize > 3 ? C.getW(i) : 1; }
      col[o * 4] = Math.round(Math.min(1, r * sr) * 255);
      col[o * 4 + 1] = Math.round(Math.min(1, gg * sg) * 255);
      col[o * 4 + 2] = Math.round(Math.min(1, b * sb) * 255);
      col[o * 4 + 3] = Math.round(Math.min(1, a * fa) * 255);
      lay[o] = p.layer;
      if (lit && L) {
        lit[o * 4] = Math.round(L.getX(i) * 255); lit[o * 4 + 1] = Math.round(L.getY(i) * 255);
        lit[o * 4 + 2] = Math.round(L.getZ(i) * 255); lit[o * 4 + 3] = 255;
      }
      if (inst) inst[o] = instIndex;
      if (plm) plm[o] = p.plMask;
    }
    // mirrored instances (negative scale) flip the winding
    const flip = m.determinant() < 0;
    if (g.index) {
      const I = g.index;
      for (let i = 0; i < I.count; i += 3) {
        const a0 = I.getX(i) + vo, a1 = I.getX(i + 1) + vo, a2 = I.getX(i + 2) + vo;
        idx[io++] = a0; idx[io++] = flip ? a2 : a1; idx[io++] = flip ? a1 : a2;
      }
    } else {
      for (let i = 0; i < nu; i += 3) { idx[io++] = vo + i; idx[io++] = vo + (flip ? i + 2 : i + 1); idx[io++] = vo + (flip ? i + 1 : i + 2); }
    }
    vo += nu;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nrm, 4, true));
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geo.setAttribute('color', new THREE.BufferAttribute(col, 4, true));
  geo.setAttribute('aLayer', new THREE.BufferAttribute(lay, 1, false));
  if (uv1) geo.setAttribute('uv1', new THREE.BufferAttribute(uv1, 2));
  if (lit) geo.setAttribute('aLight', new THREE.BufferAttribute(lit, 4, true));
  if (plm) geo.setAttribute('aPL', new THREE.BufferAttribute(plm, 1));
  if (inst) {
    geo.setAttribute('aInst', new THREE.BufferAttribute(inst, 1, false));
    // cull table as a float texture: CULL_TEX_W instances per row
    const n = cullTable.length / 4;
    const rows = Math.max(1, Math.ceil(n / CULL_TEX_W));
    const data = new Float32Array(CULL_TEX_W * rows * 4);
    data.set(cullTable);
    const tex = new THREE.DataTexture(data, CULL_TEX_W, rows, THREE.RGBAFormat, THREE.FloatType);
    tex.minFilter = tex.magFilter = THREE.NearestFilter;
    tex.needsUpdate = true;
    geo.userData.cullTex = tex;
  }
  geo.setIndex(new THREE.BufferAttribute(nv > 65535 ? idx : Uint16Array.from(idx), 1));
  geo.computeBoundingSphere();
  geo.computeBoundingBox();
  for (const a of Object.values(geo.attributes)) (a as THREE.BufferAttribute).onUpload(freeAfterUpload);
  geo.index!.onUpload(freeAfterUpload);
  return geo;
}

// ---------------------------------------------------------------------------------------------
// materials
// ---------------------------------------------------------------------------------------------
const WORLD_VERT = /* glsl */ `
attribute vec4 color;
attribute float aLayer;
// normal arrives as a normalized vec4 (Int8x4)
#ifdef USE_LM
attribute vec2 uv1;
out vec2 vUv1;
#endif
#ifdef USE_VL
attribute vec4 aLight;
out vec3 vLight;
#endif
#ifdef USE_CULL
attribute float aInst;
uniform highp sampler2D uCullTex;
uniform float uCullScale;
#endif
out vec3 vWorldPos;
out vec3 vNormal;
out vec2 vUv;
out vec4 vColor;
flat out float vLayer;
#ifdef USE_PLIGHTS
attribute float aPL;
flat out float vPL;
#endif
void main() {
#ifdef USE_PLIGHTS
  vPL = aPL;
#endif
#ifdef USE_CULL
  // engine static-model cullDist (per instance, LOD0 only)
  int ii = int(aInst + 0.5);
  vec4 aCull = texelFetch(uCullTex, ivec2(ii % 256, ii / 256), 0);
  if (aCull.w > 0.0 && distance(aCull.xyz, cameraPosition) > aCull.w * uCullScale) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
#endif
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vWorldPos = wp.xyz;
  vNormal = normalize(mat3(modelMatrix) * normal);
  vUv = uv;
  vColor = color;
  vLayer = aLayer;
#ifdef USE_LM
  vUv1 = uv1;
#endif
#ifdef USE_VL
  vLight = aLight.rgb;
#endif
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;

const WORLD_FRAG = /* glsl */ `
precision highp float;
precision highp sampler2DArray;
${BO2_FRAG_COMMON}
#ifdef USE_LM
${BO2_LIGHTMAP}
in vec2 vUv1;
#endif
#ifdef USE_VL
in vec3 vLight;
uniform float uLmScale;
#endif
uniform sampler2DArray uColorArr;
#ifdef USE_NORMALMAP
uniform sampler2DArray uNormalArr;
#endif
uniform float uAlphaCut;
uniform float uPLScale;
in vec3 vWorldPos;
in vec3 vNormal;
in vec2 vUv;
in vec4 vColor;
flat in float vLayer;
#ifdef USE_PLIGHTS
flat in float vPL;
#endif
void main() {
  vec4 tex = texture(uColorArr, vec3(vUv, vLayer));
  float alpha = tex.a * vColor.a;
#ifdef USE_ALPHATEST
  if (tex.a < uAlphaCut) discard;
  alpha = 1.0;
#endif
  vec3 base = tex.rgb * vColor.rgb;
  base *= base; // (colorMap * vertexColor)^2
#ifdef UNLIT
  // unlit techniques show the authored colour (ESTIMATE: scaled so sqrt(col * exposure) == colour)
  vec3 col = base / uExposure;
#else
  vec3 N = normalize(vNormal);
  if (!gl_FrontFacing) N = -N;
#ifdef USE_NORMALMAP
  vec3 mapN = texture(uNormalArr, vec3(vUv, vLayer)).xyz * 2.0 - 1.0;
  N = bo2BumpNormal(N, vWorldPos, vUv, mapN);
#endif
#if defined(USE_LM)
  vec3 E = bo2Lightmap(vUv1, bo2ToCod(N));
#elif defined(USE_VL)
  vec3 E = vLight * uLmScale;
#else
  vec3 E = vec3(1.0);
#endif
#ifdef USE_PLIGHTS
  int plm = int(vPL + 0.5);
  if ((plm & 2) != 0) {
    // sun (primary light 1): real time, shadowed [meta.lighting.sun.note]
    float ndl = max(dot(N, uSunDir), 0.0);
    if (ndl > 0.0) E += uSunColor * (ndl * bo2SunShadow(vWorldPos, normalize(vNormal)));
  }
#endif
  E += bo2DynLight(vWorldPos, N);
#ifdef USE_PLIGHTS
  E += bo2PrimaryLights(vPL, vWorldPos, N) * uPLScale;
#endif
  vec3 col = base * E;
#endif
  col = bo2Fog(col, vWorldPos);
#if defined(BLEND_MUL)
  gl_FragColor = vec4(mix(vec3(1.0), tex.rgb, alpha), 1.0);
#elif defined(BLEND_PREMUL) || defined(BLEND_ADD)
  vec4 o = bo2Out(col, alpha);
  gl_FragColor = vec4(o.rgb * alpha, alpha);
#else
  gl_FragColor = bo2Out(col, alpha);
#endif
}
`;

function createWorldMaterial(grp: Group, colorArr: THREE.DataArrayTexture, normalArr: THREE.DataArrayTexture | null, U: Bo2Uniforms, cullTex: THREE.DataTexture | null): THREE.ShaderMaterial {
  const defines: Record<string, string> = {};
  if (grp.light === 'lm') defines.USE_LM = '';
  if (grp.light === 'vl') defines.USE_VL = '';
  if (grp.light === 'unlit') defines.UNLIT = '';
  if (grp.model) defines.USE_CULL = '';
  if (grp.primaryLights) defines.USE_PLIGHTS = '';
  if (normalArr) defines.USE_NORMALMAP = '';
  if (grp.alpha === 'MASK') defines.USE_ALPHATEST = '';
  if (grp.blend === 'premul') defines.BLEND_PREMUL = '';
  if (grp.blend === 'add') defines.BLEND_ADD = '';
  if (grp.blend === 'mul') defines.BLEND_MUL = '';
  const m = new THREE.ShaderMaterial({
    name: `bo2_${grp.key}`,
    defines,
    uniforms: {
      ...U,
      uColorArr: { value: colorArr },
      uNormalArr: { value: normalArr },
      uAlphaCut: { value: grp.cutoff },
      uCullTex: { value: cullTex },
      uPLScale: U.uPLScale,
    },
    vertexShader: WORLD_VERT,
    fragmentShader: WORLD_FRAG,
    side: grp.doubleSided ? THREE.DoubleSide : THREE.FrontSide,
    transparent: grp.alpha === 'BLEND',
    depthWrite: grp.depthWrite,
  });
  if (grp.alpha === 'BLEND') {
    m.blending = THREE.CustomBlending;
    m.blendEquation = THREE.AddEquation;
    switch (grp.blend) {
      case 'premul': m.blendSrc = THREE.OneFactor; m.blendDst = THREE.OneMinusSrcAlphaFactor; break;
      case 'add': m.blendSrc = THREE.OneFactor; m.blendDst = THREE.OneFactor; break;
      case 'mul': m.blendSrc = THREE.ZeroFactor; m.blendDst = THREE.SrcColorFactor; break;
      case 'none': m.blending = THREE.NoBlending; break;
      default: m.blendSrc = THREE.SrcAlphaFactor; m.blendDst = THREE.OneMinusSrcAlphaFactor;
    }
  }
  if (grp.polyOffset) {
    // ESTIMATE: T6 'offset1'/'offset2' decal depth bias
    m.polygonOffset = true;
    m.polygonOffsetFactor = -grp.polyOffset;
    m.polygonOffsetUnits = -2 * grp.polyOffset;
  }
  return m;
}

const DEPTH_VERT = /* glsl */ `
attribute float aLayer;
out vec2 vUv;
flat out float vLayer;
void main() {
  vUv = uv; vLayer = aLayer;
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.0);
}
`;
const DEPTH_FRAG = /* glsl */ `
precision highp float;
precision highp sampler2DArray;
uniform sampler2DArray uColorArr;
uniform float uAlphaCut;
in vec2 vUv;
flat in float vLayer;
void main() {
#ifdef USE_ALPHATEST
  if (texture(uColorArr, vec3(vUv, vLayer)).a < uAlphaCut) discard;
#endif
  gl_FragColor = vec4(1.0);
}
`;

function createDepthMaterial(grp: Group, colorArr: THREE.DataArrayTexture): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    name: `bo2_depth_${grp.key}`,
    defines: grp.alpha === 'MASK' ? { USE_ALPHATEST: '' } : {},
    uniforms: { uColorArr: { value: colorArr }, uAlphaCut: { value: grp.cutoff } },
    vertexShader: DEPTH_VERT,
    fragmentShader: DEPTH_FRAG,
    side: THREE.DoubleSide, // two-sided casters: thin geometry (rails, awnings) would leak otherwise
    colorWrite: false,
  });
}

// ---------------------------------------------------------------------------------------------
// water (wpc/water_ocean_mp_hijacked*, wpc/mtl_water_karma_pool): techniques wpc_cod7watershore_* /
// wpc_cod7water_* are not ported; this is a stand-in driven by the material constants
// (waterColorShallow, waterColorDeepDelta, normalUVcontrol*/normalUVscroll, customReflectionProbeScale).
// ---------------------------------------------------------------------------------------------
const WATER_VERT = /* glsl */ `
out vec3 vWorldPos;
out vec3 vNormal;
void main() {
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vWorldPos = wp.xyz;
  vNormal = normalize(mat3(modelMatrix) * normal);
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;
const WATER_FRAG = /* glsl */ `
precision highp float;
${BO2_FRAG_COMMON}
uniform sampler2D uWaterNormal;
uniform samplerCube uSkyCube;
uniform vec3 uSkyRot;
uniform float uTime;
uniform vec3 uShallow;
uniform vec3 uDeep;
uniform vec4 uUv0; // scale0 (1/in), scroll0 ...
uniform vec4 uUv1;
uniform float uAlpha;
uniform float uReflScale;
in vec3 vWorldPos;
in vec3 vNormal;
vec3 skyRadiance(vec3 dirThree) {
  vec3 p = bo2ToCod(dirThree);
  vec3 v = vec3(p.x * uSkyRot.y + p.y * uSkyRot.z, p.x * uSkyRot.x + p.y * uSkyRot.y, p.z);
  vec3 c = texture(uSkyCube, v).rgb;
  return c * c / uExposure; // LDR sky (rgb^2) expressed in scene units (so the sky shows at its authored colour)
}
void main() {
  vec2 xz = vec2(vWorldPos.x, -vWorldPos.z); // CoD xy
  vec2 uvA = xz * uUv0.x + vec2(uTime * uUv0.z, uTime * uUv0.w);
  vec2 uvB = xz * uUv1.x + vec2(-uTime * uUv1.z, uTime * uUv1.w * 0.7);
  vec3 nA = texture(uWaterNormal, uvA).xyz * 2.0 - 1.0;
  vec3 nB = texture(uWaterNormal, uvB).xyz * 2.0 - 1.0;
  vec2 slope = (nA.xy * 0.52 + nB.xy * 0.30); // normalScaleLoHi
  vec3 N = normalize(vec3(slope.x, 1.0, -slope.y));
  vec3 V = normalize(cameraPosition - vWorldPos);
  float ndv = max(dot(N, V), 0.0);
  float fres = 0.02 + 0.98 * pow(1.0 - ndv, 5.0);
  vec3 R = reflect(-V, N);
  // reflections never sample the horizon rows of the cube (painted silhouettes there would smear into long
  // vertical streaks across the water at grazing angles)
  R.y = max(abs(R.y), 0.06);
  R = normalize(R);
  vec3 refl = skyRadiance(R) * uReflScale;
  // body colour lit by the sun + sky, deep tint at grazing angles
  vec3 body = mix(uShallow, uDeep, 1.0 - ndv);
  body *= body;
  vec3 E = vec3(1.0) + uSunColor * max(dot(vec3(0.0, 1.0, 0.0), uSunDir), 0.0) * bo2SunShadow(vWorldPos, vec3(0.0, 1.0, 0.0));
  vec3 col = mix(body * E, refl, fres);
  // sun glitter (ESTIMATE)
  vec3 H = normalize(uSunDir + V);
  float dist = length(cameraPosition - vWorldPos);
  col += uSunColor * pow(max(dot(N, H), 0.0), 600.0) * 1.2 * clamp(1.0 - dist / 20000.0, 0.0, 1.0) * bo2SunShadow(vWorldPos, vec3(0.0, 1.0, 0.0));
  col = bo2Fog(col, vWorldPos);
  gl_FragColor = bo2Out(col, mix(uAlpha, 1.0, fres));
}
`;

function createWaterMesh(src: THREE.Mesh, U: Bo2Uniforms): THREE.Mesh {
  const g = src.geometry.clone();
  g.applyMatrix4(src.matrixWorld);
  for (const k of Object.keys(g.attributes)) if (k !== 'position' && k !== 'normal') g.deleteAttribute(k);
  g.computeBoundingSphere();
  const sm = src.material as THREE.MeshStandardMaterial;
  const c = ((sm.userData as { bo2?: Bo2MatExtras }).bo2?.constants) ?? {};
  const shallow = c.waterColorShallow ?? [0.107, 0.448, 0.574];
  const dd = c.waterColorDeepDelta ?? [-0.073, -0.384, -0.441];
  const nuv0 = c.normalUVcontrol0 ?? [0.000588, 0.000625, 0, 1];
  const nuv2 = c.normalUVcontrol2 ?? [0.00286, 0.00277, 1, 0];
  const scroll = c.normalUVscroll_0123 ?? [0.0154, 1, 0.05, 0.25];
  const nt = sm.normalMap ?? null;
  if (nt) { nt.wrapS = nt.wrapT = THREE.RepeatWrapping; nt.colorSpace = THREE.NoColorSpace; nt.needsUpdate = true; }
  const m = new THREE.ShaderMaterial({
    name: 'bo2_water',
    uniforms: {
      ...U,
      uWaterNormal: { value: nt },
      uShallow: { value: new THREE.Vector3(Math.sqrt(Math.max(0, shallow[0])), Math.sqrt(Math.max(0, shallow[1])), Math.sqrt(Math.max(0, shallow[2]))) },
      uDeep: { value: new THREE.Vector3(Math.sqrt(Math.max(0, shallow[0] + dd[0])), Math.sqrt(Math.max(0, shallow[1] + dd[1])), Math.sqrt(Math.max(0, shallow[2] + dd[2]))) },
      uUv0: { value: new THREE.Vector4(nuv0[0] * 4, 0, scroll[0] * 0.5, scroll[2] * 0.2) },
      uUv1: { value: new THREE.Vector4(nuv2[0], 0, scroll[2] * 0.3, scroll[0]) },
      uAlpha: { value: sm.opacity ?? 0.85 },
      uReflScale: { value: 0.55 }, // ESTIMATE: the game reflects a darker water cubemap (skybox_sp_hijacked_water_ft, not exported)
    },
    vertexShader: WATER_VERT,
    fragmentShader: WATER_FRAG,
    transparent: true,
    depthWrite: true,
  });
  const mesh = new THREE.Mesh(g, m);
  mesh.name = `water_${sm.name}`;
  mesh.renderOrder = 5;
  mesh.matrixAutoUpdate = false;
  return mesh;
}
